import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

import {
  DOCUMENT_NAME_MAX,
  DOCUMENT_PERMISSIONS,
  documentCan,
  useDuplicateDocument,
  useRenameDocument,
  useSetDocumentFavorite,
  useTrashDocument,
  type WorkspaceDocument,
} from '@/entities/document';
import {
  type CardMenuAction,
  documentTileData,
  type DraggedResource,
  useCardDrag,
} from '@/entities/section';
import { FormError } from '@/shared/forms';
import { useErrorMessage } from '@/shared/i18n';
import { ResourceLabelChips } from '@/features/resource-labels';
import { originStateFrom } from '@/shared/lib/origin';
import {
  Button,
  CardActions,
  ConfirmDialog,
  Dialog,
  DialogActions,
  Icons,
  KindIcon,
  MenuItem,
  MenuList,
  MenuRow,
  MetaItem,
  Popover,
  ResourceMeta,
  TextField,
  useToast,
} from '@/shared/ui';

import { SaveTemplateDialog } from '@/features/template-manage';
import { DocumentSettingsDialog } from './DocumentSettingsDialog';
import styles from './DocumentCatalog.module.css';

export function RenameDocumentDialog({
  document,
  onClose,
}: {
  document: WorkspaceDocument;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const rename = useRenameDocument(document.id);
  const [name, setName] = useState(document.name);
  return (
    <Dialog title={t('documents.rename')} onClose={onClose}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) rename.mutate(name.trim(), { onSuccess: onClose });
        }}
      >
        <TextField
          label={t('documents.name')}
          value={name}
          maxLength={DOCUMENT_NAME_MAX}
          autoFocus
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <FormError error={rename.error} />
        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={rename.isPending}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** One note card; its leading icon (a page) tells it from a board and a database. */
export function DocumentCard({
  document,
  location,
  previous,
  next,
  onMoveToSection,
  onDropBeside,
  onCombine,
  groupMenu,
}: {
  document: WorkspaceDocument;
  location?: string | undefined;
  previous?: WorkspaceDocument | undefined;
  next?: WorkspaceDocument | undefined;
  onMoveToSection?: (() => void) | undefined;
  onDropBeside?:
    ((sourceId: string, anchor: { after_id: string } | { before_id: string }) => void) | undefined;
  onCombine?: ((source: DraggedResource) => void) | undefined;
  groupMenu?: readonly CardMenuAction[] | undefined;
}) {
  const { t } = useTranslation();
  const here = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const message = useErrorMessage();
  const ref = useRef<HTMLLIElement>(null);
  const [renaming, setRenaming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const favorite = useSetDocumentFavorite(document);
  const duplicate = useDuplicateDocument();
  const trash = useTrashDocument();
  const mayEdit = documentCan(document, DOCUMENT_PERMISSIONS.editDetails);
  const mayDelete = documentCan(document, DOCUMENT_PERMISSIONS.delete);
  const dragEnabled = onDropBeside !== undefined && mayEdit;
  const { dragging, zone } = useCardDrag({
    ref,
    enabled: dragEnabled,
    kind: 'document',
    id: document.id,
    workspaceId: document.workspace_id,
    data: documentTileData(document.workspace_id, document.id),
    previousId: previous?.id,
    nextId: next?.id,
    onDropBeside,
    onCombine,
  });
  return (
    <li
      ref={ref}
      className={[
        styles.card,
        dragEnabled && styles.cardDraggable,
        dragging && styles.cardDragging,
        zone && zone !== 'combine' && styles[`cardDrop-${zone}`],
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="document-card"
      data-combine={zone === 'combine' || undefined}
    >
      <Link
        to={`/app/documents/${document.id}`}
        state={originStateFrom(here)}
        className={styles.cardLink}
        draggable={dragEnabled ? false : undefined}
      >
        <span className={styles.cardName}>
          <KindIcon icon={<Icons.document />} label={t('workspace.objectTypes.document')} />
          <span className={styles.cardTitle} title={document.name}>
            {document.name}
          </span>
        </span>
        {location && (
          <span className={styles.cardLocation} data-testid="resource-location">
            <Icons.folder /> {location}
          </span>
        )}
        <ResourceMeta>
          <MetaItem icon={<Icons.crown />}>
            {t('boards.ownerName', { name: document.owner_name })}
          </MetaItem>
        </ResourceMeta>
        <ResourceLabelChips labels={document.labels} label={t('resourceLabels.field')} />
      </Link>
      <CardActions>
        <button
          type="button"
          className={styles.favorite}
          aria-pressed={document.is_favorite}
          aria-label={t(document.is_favorite ? 'documents.unfavorite' : 'documents.favorite', {
            name: document.name,
          })}
          disabled={favorite.isPending}
          onClick={() => {
            favorite.mutate(!document.is_favorite);
          }}
        >
          {document.is_favorite ? '★' : '☆'}
        </button>
        <Popover
          label={t('documents.menu', { name: document.name })}
          width={240}
          alignEnd
          trigger={(props) => (
            <button
              type="button"
              {...props}
              className={styles.cardMenu}
              aria-label={t('documents.menu', { name: document.name })}
              data-testid="document-menu"
            >
              <Icons.more />
            </button>
          )}
        >
          {(close) => (
            <MenuList>
              <MenuRow>
                <MenuItem
                  icon={<Icons.layers />}
                  onClick={() => {
                    close();
                    setSavingTemplate(true);
                  }}
                >
                  {t('templates.action')}
                </MenuItem>
              </MenuRow>
              {(mayEdit || documentCan(document, DOCUMENT_PERMISSIONS.manageAccess)) && (
                <MenuRow>
                  <MenuItem
                    icon={<Icons.sliders />}
                    onClick={() => {
                      close();
                      setEditing(true);
                    }}
                  >
                    {t('catalog.editResource')}
                  </MenuItem>
                </MenuRow>
              )}
              {mayEdit && (
                <MenuRow>
                  <MenuItem
                    icon={<Icons.edit />}
                    onClick={() => {
                      close();
                      setRenaming(true);
                    }}
                  >
                    {t('documents.rename')}
                  </MenuItem>
                </MenuRow>
              )}
              {onMoveToSection && (
                <MenuRow>
                  <MenuItem
                    icon={<Icons.folder />}
                    onClick={() => {
                      close();
                      onMoveToSection();
                    }}
                  >
                    {t('sections.moveTo')}
                  </MenuItem>
                </MenuRow>
              )}
              {groupMenu?.map((action) => (
                <MenuRow key={action.id}>
                  <MenuItem
                    icon={action.icon}
                    onClick={() => {
                      close();
                      action.onSelect();
                    }}
                  >
                    {action.label}
                  </MenuItem>
                </MenuRow>
              ))}
              <MenuRow>
                <MenuItem
                  icon={<Icons.copy />}
                  onClick={() => {
                    close();
                    duplicate.mutate(document.id, {
                      onSuccess: (copy) => {
                        void navigate(`/app/documents/${copy.id}`, {
                          state: originStateFrom(here),
                        });
                      },
                      onError: (error) => {
                        toast.show({ message: message(error) });
                      },
                    });
                  }}
                >
                  {t('documents.duplicate')}
                </MenuItem>
              </MenuRow>
              {mayDelete && (
                <MenuRow separated>
                  <MenuItem
                    icon={<Icons.trash />}
                    danger
                    onClick={() => {
                      close();
                      setDeleting(true);
                    }}
                  >
                    {t('documents.delete')}
                  </MenuItem>
                </MenuRow>
              )}
            </MenuList>
          )}
        </Popover>
      </CardActions>
      {savingTemplate && (
        <SaveTemplateDialog
          kind="document"
          sourceId={document.id}
          defaultName={document.name}
          onClose={() => {
            setSavingTemplate(false);
          }}
        />
      )}
      {editing && (
        <DocumentSettingsDialog
          document={document}
          onClose={() => {
            setEditing(false);
          }}
        />
      )}
      {renaming && (
        <RenameDocumentDialog
          document={document}
          onClose={() => {
            setRenaming(false);
          }}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title={t('documents.deleteTitle')}
          message={t('documents.deleteMessage', { name: document.name })}
          confirmLabel={t('documents.delete')}
          pending={trash.isPending}
          onCancel={() => {
            setDeleting(false);
          }}
          onConfirm={() => {
            trash.mutate(document.id, {
              onSettled: () => {
                setDeleting(false);
              },
              onError: (error) => {
                toast.show({ message: message(error) });
              },
            });
          }}
        />
      )}
    </li>
  );
}
